import type { AssetReportResponse } from '~~/server/modules/reports/reports'
import {
  DIAMETER_BUCKET_MAX,
  DIAMETER_IMPERIAL_BOUNDARIES,
  DIAMETER_METRIC_BOUNDARIES
} from '~~/shared/constants/diameterBuckets'
import { MATERIAL_CODE_TO_KEY, type MaterialKey } from '~/constants/materials'
export type LengthUnit = 'km' | 'mi'

export interface AssetChartDatum {
  label: string
  labelKey?: string
  value: number
  color: string
}

export interface AssetChartSeries {
  label: string
  labelKey?: string
  color: string
  values: number[]
}

export interface AssetStackedChartData {
  categories: string[]
  series: AssetChartSeries[]
}

const DIAMETER_BAR_COLORS = [
  '#BBDEFB',
  '#64B5F6',
  '#2196F3',
  '#1976D2',
  '#0D47A1',
  '#0a3982',
  '#2D039F',
  '#1F0073'
] as const

const MATERIAL_PRESENTATION_BY_KEY = {
  CAST_IRON: { label: 'CAST_IRON', color: '#512DA8' },
  DUCTILE_IRON: { label: 'DUCTILE_IRON', color: '#00796B' },
  STEEL: { label: 'STEEL', color: '#F57C00' },
  ASBESTOS_CEMENT: { label: 'ASBESTOS_CEMENT', color: '#0288D1' },
  PLASTIC: { label: 'PLASTIC', color: '#E64A19' },
  PVC: { label: 'PVC', color: '#FFCC80' },
  PEHDPE: { label: 'PEHDPE', color: '#325ea8' },
  PCRC: { label: 'PCRC', color: '#8132a8' },
  CONCRETE: { label: 'CONCRETE', color: '#616161' },
  OTHER: { label: 'OTHER', color: '#FBC02D' },
  UNKNOWN: { label: 'UNKNOWN', color: '#607D8B' }
} as const

const DEFAULT_MATERIAL_PRESENTATION = MATERIAL_PRESENTATION_BY_KEY.UNKNOWN
const UNKNOWN_MATERIAL_CODE = 6

const MATERIAL_CODES = Object.keys(MATERIAL_CODE_TO_KEY)
  .map((code) => Number(code))
  .sort((left, right) => left - right)

const MILES_PER_METER = 0.000621371192237334
const KILOMETERS_PER_METER = 0.001
const PRIMARY_BREAK_BAR_COLOR = '#3e9bb9'

const getDiameterBoundaries = (country: string | null | undefined) => {
  return country === 'US' ? DIAMETER_IMPERIAL_BOUNDARIES : DIAMETER_METRIC_BOUNDARIES
}

export const getDiameterUnitLabel = (country: string | null | undefined) => {
  return country === 'US' ? 'in' : 'mm'
}

export const getLengthUnitLabel = (country: string | null | undefined): LengthUnit => {
  return country === 'US' ? 'mi' : 'km'
}

export const roundChartValue = (value: number) => {
  return Math.round((value + Number.EPSILON) * 100) / 100
}

export const formatChartValue = (value: number) => {
  return new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(roundChartValue(value))
}

export const formatCountValue = (value: number) => {
  return new Intl.NumberFormat(undefined, {
    maximumFractionDigits: 0
  }).format(value)
}

export const convertLengthForDisplay = (
  valueInMeters: number,
  country: string | null | undefined
) => {
  const convertedValue =
    getLengthUnitLabel(country) === 'mi'
      ? valueInMeters * MILES_PER_METER
      : valueInMeters * KILOMETERS_PER_METER

  return roundChartValue(convertedValue)
}

export const getMaterialPresentation = (materialTypeCode: number) => {
  const materialKey = MATERIAL_CODE_TO_KEY[materialTypeCode] as MaterialKey | undefined

  if (!materialKey) {
    return DEFAULT_MATERIAL_PRESENTATION
  }

  return MATERIAL_PRESENTATION_BY_KEY[materialKey] ?? DEFAULT_MATERIAL_PRESENTATION
}

const normalizeMaterialTypeCode = (materialTypeCode: number) => {
  return MATERIAL_CODE_TO_KEY[materialTypeCode] ? materialTypeCode : UNKNOWN_MATERIAL_CODE
}

export const getMaterialLabelKey = (label: string) => {
  return `components.pages.reports.asset.materialLabels.${label}`
}

const getFilledInstallYearCategories = (years: number[]) => {
  if (years.length === 0) {
    return []
  }

  const minYear = Math.min(...years)
  const maxYear = Math.max(...years)

  return Array.from({ length: maxYear - minYear + 1 }, (_, index) => String(minYear + index))
}

export const toMaterialChartData = (
  items: AssetReportResponse['data']['length_by_material'],
  country: string | null | undefined
): AssetChartDatum[] => {
  const totalLengthByMaterialCode = new Map<number, number>()

  items.forEach((item) => {
    const normalizedMaterialCode = normalizeMaterialTypeCode(item.material_type_code)

    totalLengthByMaterialCode.set(
      normalizedMaterialCode,
      (totalLengthByMaterialCode.get(normalizedMaterialCode) ?? 0) + item.total_length
    )
  })

  return MATERIAL_CODES.map((materialTypeCode) => {
    const presentation = getMaterialPresentation(materialTypeCode)

    return {
      label: presentation.label,
      labelKey: getMaterialLabelKey(presentation.label),
      value: convertLengthForDisplay(totalLengthByMaterialCode.get(materialTypeCode) ?? 0, country),
      color: presentation.color
    }
  })
}

export const toInstallYearMaterialLengthChartData = (
  items: AssetReportResponse['data']['length_by_install_year_and_material'],
  country: string | null | undefined
): AssetStackedChartData => {
  const categories = getFilledInstallYearCategories(items.map((item) => item.install_year))
  const totalLengthByYear = new Map<number, Map<number, number>>()

  items.forEach((item) => {
    const totalLengthByMaterialCode =
      totalLengthByYear.get(item.install_year) ?? new Map<number, number>()

    item.materials.forEach((material) => {
      const normalizedMaterialCode = normalizeMaterialTypeCode(material.material_type_code)

      totalLengthByMaterialCode.set(
        normalizedMaterialCode,
        (totalLengthByMaterialCode.get(normalizedMaterialCode) ?? 0) + material.total_length
      )
    })

    totalLengthByYear.set(item.install_year, totalLengthByMaterialCode)
  })

  const series = MATERIAL_CODES.map((materialTypeCode) => {
    const presentation = getMaterialPresentation(materialTypeCode)
    const values = categories.map((category) => {
      const year = Number(category)
      const value = totalLengthByYear.get(year)?.get(materialTypeCode) ?? 0

      return convertLengthForDisplay(value, country)
    })

    return {
      label: presentation.label,
      labelKey: getMaterialLabelKey(presentation.label),
      color: presentation.color,
      values
    }
  })

  return {
    categories,
    series
  }
}

export const toInstallYearMaterialBreakChartData = (
  items: AssetReportResponse['data']['breaks_by_install_year_and_material']
): AssetStackedChartData => {
  const categories = getFilledInstallYearCategories(items.map((item) => item.install_year))
  const breakCountByYear = new Map<number, Map<number, number>>()

  items.forEach((item) => {
    const breakCountByMaterialCode =
      breakCountByYear.get(item.install_year) ?? new Map<number, number>()

    item.materials.forEach((material) => {
      const normalizedMaterialCode = normalizeMaterialTypeCode(material.material_type_code)

      breakCountByMaterialCode.set(
        normalizedMaterialCode,
        (breakCountByMaterialCode.get(normalizedMaterialCode) ?? 0) + material.break_count
      )
    })

    breakCountByYear.set(item.install_year, breakCountByMaterialCode)
  })

  const series = MATERIAL_CODES.map((materialTypeCode) => {
    const presentation = getMaterialPresentation(materialTypeCode)
    const values = categories.map((category) => {
      const year = Number(category)

      return breakCountByYear.get(year)?.get(materialTypeCode) ?? 0
    })

    return {
      label: presentation.label,
      labelKey: getMaterialLabelKey(presentation.label),
      color: presentation.color,
      values
    }
  })

  return {
    categories,
    series
  }
}

export const toBreaksByYearChartData = (
  items: AssetReportResponse['data']['breaks_by_year']
): AssetChartDatum[] => {
  return [...items]
    .sort((left, right) => left.year - right.year)
    .map((item) => ({
      label: String(item.year),
      value: item.break_count,
      color: PRIMARY_BREAK_BAR_COLOR
    }))
}

export const toBreaksByInstallYearDecadeChartData = (
  items: AssetReportResponse['data']['breaks_by_install_year_decade']
): AssetChartDatum[] => {
  return [...items]
    .sort((left, right) => left.start - right.start)
    .map((item) => ({
      label: `${item.start}-${item.end}`,
      value: item.break_count,
      color: PRIMARY_BREAK_BAR_COLOR
    }))
}

export const formatDiameterBucketLabel = (
  min: number,
  max: number,
  country: string | null | undefined
) => {
  const boundaries = getDiameterBoundaries(country)
  const unit = getDiameterUnitLabel(country)
  const firstBoundary = boundaries[0]

  if (min === firstBoundary) {
    return `< ${max}${unit}`
  }

  if (max === DIAMETER_BUCKET_MAX) {
    return `≥ ${min}${unit}`
  }

  return `${min}${unit} - ${max}${unit}`
}

export const toDiameterChartData = (
  items: AssetReportResponse['data']['length_by_diameter'],
  country: string | null | undefined
): AssetChartDatum[] => {
  return items.map((item, index) => ({
    label: formatDiameterBucketLabel(item.min, item.max, country),
    value: convertLengthForDisplay(item.total_length, country),
    color: DIAMETER_BAR_COLORS[index] ?? '#000000'
  }))
}
